import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/App";
import { BRAND } from "@/styles/brand";
import CouponCard from "@/components/loyalty/CouponCard";
import StampCard from "@/components/loyalty/StampCard";
import TierBadge from "@/components/loyalty/TierBadge";
import StreakCounter from "@/components/loyalty/StreakCounter";
import AchievementBadge from "@/components/loyalty/AchievementBadge";
import ScratchCard from "@/components/loyalty/ScratchCard";
import WeeklyChallenge from "@/components/loyalty/WeeklyChallenge";
import { ChevronRight, Gift, Star, Flame, Trophy, Ticket } from "lucide-react";

const C = BRAND.colors;

// ═══════════════════════════════════════════════════════════
// EXAMPLE DATA (would come from API in production)
// ═══════════════════════════════════════════════════════════

const TABS = [
  { id: "overview", label: "Uebersicht", emoji: "🏠" },
  { id: "coupons", label: "Coupons", emoji: "🎫" },
  { id: "stamps", label: "Stempel", emoji: "☕" },
  { id: "challenges", label: "Challenges", emoji: "🎯" },
  { id: "badges", label: "Badges", emoji: "🏆" },
];

const sampleCoupons = [
  {
    id: "c1", type: "welcome", title: "Willkommens-Matcha",
    description: "Dein erster Matcha Latte geht auf uns!",
    discount: "GRATIS", validUntil: "2026-04-10",
    conditions: "Einmal einloesbar, alle Matcha-Varianten", status: "active",
  },
  {
    id: "c2", type: "percent", title: "Mittwochs-Deal",
    description: "20% auf alle Cheesecake-Stuecke",
    discount: "20%", validUntil: "2026-03-28",
    conditions: "Nur mittwochs, nicht kombinierbar", status: "expiring",
  },
  {
    id: "c3", type: "free", title: "Streak-Belohnung",
    description: "7 Tage in Folge da - Gratis Topping!",
    discount: "GRATIS", validUntil: "2026-04-15",
    conditions: "Auf jedes Getränk", status: "active",
  },
  {
    id: "c4", type: "mystery", title: "Mystery Box",
    description: "Kratze und entdecke deinen Überraschungs-Reward!",
    discount: "???", validUntil: "2026-04-05",
    conditions: "Lass dich überraschen", status: "active",
  },
  {
    id: "c5", type: "percent", title: "Pasta Donnerstag",
    description: "15% auf alle Pasta-Gerichte",
    discount: "15%", validUntil: "2026-03-20",
    conditions: "Nur donnerstags", status: "expired",
  },
];

const sampleChallenges = [
  {
    id: "ch1", title: "Matcha Marathon", emoji: "🍵", type: "product",
    description: "Bestelle 3x Matcha diese Woche",
    reward: "+100 Bonus-Punkte", progress: 2, maxProgress: 3,
    endsAt: "2026-03-30T23:59:00",
  },
  {
    id: "ch2", title: "Cheesecake-Weltreise", emoji: "🍰", type: "product",
    description: "Probiere 4 verschiedene Cheesecake-Sorten",
    reward: "Gratis Cheesecake", progress: 1, maxProgress: 4,
    endsAt: "2026-04-06T23:59:00",
  },
  {
    id: "ch3", title: "Fruehstuecks-Held", emoji: "🌅", type: "visit",
    description: "Besuche uns 3x vor 10 Uhr",
    reward: "Gratis Croissant", progress: 1, maxProgress: 3,
    endsAt: "2026-04-02T23:59:00",
  },
  {
    id: "ch4", title: "Social Butterfly", emoji: "🦋", type: "social",
    description: "Empfiehl Franky's Farm an einen Freund",
    reward: "+200 Punkte fuer euch beide", progress: 0, maxProgress: 1,
    endsAt: "2026-04-30T23:59:00",
  },
];

const sampleAchievements = [
  { id: "a1", name: "Erster Bissen", description: "Deine erste Bestellung bei Franky's", emoji: "🍴", unlocked: true },
  { id: "a2", name: "Matcha Meister", description: "10x Matcha bestellt", emoji: "🍵", unlocked: true },
  { id: "a3", name: "Stammgast", description: "20 Besuche", emoji: "🪑", unlocked: true },
  { id: "a4", name: "Early Bird", description: "5x vor 9 Uhr bestellt", emoji: "🐦", unlocked: false, progress: 3, maxProgress: 5 },
  { id: "a5", name: "Cheesecake Connoisseur", description: "Alle Sorten probiert", emoji: "🍰", unlocked: false, progress: 2, maxProgress: 5 },
  { id: "a6", name: "Social Butterfly", description: "3 Freunde empfohlen", emoji: "🦋", unlocked: false, progress: 1, maxProgress: 3 },
  { id: "a7", name: "7-Tage-Flamme", description: "7 Tage Streak", emoji: "🔥", unlocked: true },
  { id: "a8", name: "Pasta Profi", description: "Jede Pasta probiert", emoji: "🍝", unlocked: false, progress: 2, maxProgress: 4 },
  { id: "a9", name: "Monats-Monster", description: "30 Tage Streak", emoji: "👑", unlocked: false, progress: 12, maxProgress: 30 },
  { id: "a10", name: "Entdecker", description: "Alles auf der Karte probiert", emoji: "🗺️", unlocked: false, progress: 8, maxProgress: 20 },
  { id: "a11", name: "Nachtschwaermer", description: "5x nach 18 Uhr bestellt", emoji: "🌙", unlocked: false, progress: 2, maxProgress: 5 },
  { id: "a12", name: "Farm-Legende", description: "Farmer-Tier erreicht", emoji: "🌾", unlocked: false },
];

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export default function LoyaltyV2Page() {
  const { user, setShowAuthModal } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [showScratchCard, setShowScratchCard] = useState(false);

  const points = user?.points || 1250;
  const userName = user?.name?.split(" ")[0] || "Gast";

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 11) return "Guten Morgen";
    if (h < 14) return "Mahlzeit";
    if (h < 18) return "Hallo";
    return "Guten Abend";
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ backgroundColor: C.bg }}>
        <img src="/loyalty/tier-saemling.png" alt="Saemling"
          className="w-32 h-32 object-contain mb-6" />
        <h1 className="text-2xl font-bold mb-2"
          style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
          Werde Teil der Farm-Familie!
        </h1>
        <p className="text-sm mb-6" style={{ color: C.textMuted }}>
          Sammle Punkte, loese Rewards ein und geniesse exklusive Vorteile.
        </p>
        <motion.button whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.02 }}
          onClick={() => {
            document.dispatchEvent(new CustomEvent("set-auth-mode", { detail: "register" }));
            setShowAuthModal(true);
          }}
          className="w-full max-w-xs py-4 rounded-2xl font-bold text-white text-base"
          style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)` }}>
          Kostenlos registrieren
        </motion.button>
        <button onClick={() => setShowAuthModal(true)}
          className="mt-3 text-sm font-semibold" style={{ color: C.green }}>
          Schon Mitglied? Anmelden
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: C.bg }}>

      {/* Header */}
      <div className="px-5 pt-6 pb-3">
        <p className="text-sm" style={{ color: C.textMuted }}>{getGreeting()},</p>
        <h1 className="text-[22px] font-bold"
          style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
          {userName}! 👋
        </h1>
      </div>

      {/* Points Hero Card */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden p-5 text-white"
          style={{
            borderRadius: 24,
            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenLight} 100%)`,
            boxShadow: `0 8px 32px ${C.green}30`,
          }}>
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute right-8 bottom-2 w-16 h-16 rounded-full bg-white/5" />

          <div className="flex items-center justify-between mb-1">
            <TierBadge currentTier="gaertner" points={points} compact />
            <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full">
              <Flame size={14} className="text-orange-300" />
              <span className="text-xs font-bold">5 Tage</span>
            </div>
          </div>

          <div className="flex items-end gap-2 mt-3 mb-2">
            <span className="text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: BRAND.typography.heading }}>
              {points.toLocaleString("de-DE")}
            </span>
            <span className="text-white/60 text-sm font-medium pb-2">Punkte</span>
          </div>

          <div>
            <div className="flex justify-between text-xs text-white/70 mb-1.5">
              <span>Nächstes Tier: Farmer</span>
              <span>{points}/2000</span>
            </div>
            <div className="w-full h-2.5 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(points / 2000) * 100}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${C.tan}, ${C.tanLight || "#E8C49A"})` }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tab Navigation */}
      <div className="px-5 mb-4">
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {TABS.map((tab) => (
            <button key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={{
                backgroundColor: activeTab === tab.id ? C.green : C.bgCard,
                color: activeTab === tab.id ? "#fff" : C.textMuted,
                boxShadow: activeTab === tab.id ? `0 4px 12px ${C.green}30` : BRAND.shadows.sm,
              }}>
              <span>{tab.emoji}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="px-5">

          {/* ═══ OVERVIEW ═══ */}
          {activeTab === "overview" && (
            <div className="space-y-5">
              {/* Quick Actions */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Coupons", emoji: "🎫", count: 3, tab: "coupons" },
                  { label: "Challenges", emoji: "🎯", count: 4, tab: "challenges" },
                  { label: "Badges", emoji: "🏆", count: "3/12", tab: "badges" },
                ].map((item) => (
                  <motion.button key={item.label} whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveTab(item.tab)}
                    className="p-4 rounded-[18px] text-center"
                    style={{ backgroundColor: C.bgCard, boxShadow: BRAND.shadows.sm }}>
                    <span className="text-2xl block mb-1">{item.emoji}</span>
                    <span className="text-xl font-extrabold block"
                      style={{ color: C.text, fontFamily: BRAND.typography.heading }}>{item.count}</span>
                    <span className="text-[11px] font-medium" style={{ color: C.textMuted }}>{item.label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Streak */}
              <StreakCounter
                currentStreak={5} longestStreak={12}
                lastDays={[true, true, true, true, true, false, false]}
              />

              {/* Active Challenge Preview */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-[16px] font-bold" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    Aktive Challenges
                  </h2>
                  <button onClick={() => setActiveTab("challenges")}
                    className="text-[13px] font-semibold flex items-center gap-0.5" style={{ color: C.green }}>
                    Alle <ChevronRight size={14} />
                  </button>
                </div>
                <WeeklyChallenge challenge={sampleChallenges[0]} />
              </div>

              {/* Featured Coupon */}
              <div>
                <h2 className="text-[16px] font-bold mb-3" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                  Deine Coupons
                </h2>
                <CouponCard coupon={sampleCoupons[0]} />
              </div>

              {/* Stamp Card Preview */}
              <StampCard stamps={6} />

              {/* Mystery Scratch Card CTA */}
              <motion.button whileTap={{ scale: 0.98 }}
                onClick={() => setShowScratchCard(true)}
                className="w-full p-5 rounded-[20px] text-left flex items-center gap-4"
                style={{
                  background: `linear-gradient(135deg, ${C.tan}30, ${C.orange}20)`,
                  border: `1px dashed ${C.tan}`,
                }}>
                <span className="text-4xl">🎰</span>
                <div>
                  <h3 className="text-[15px] font-bold" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    Mystery Rubbellos
                  </h3>
                  <p className="text-[13px]" style={{ color: C.textMuted }}>
                    Kratze und entdecke deinen Überraschungs-Reward!
                  </p>
                </div>
                <ChevronRight size={20} style={{ color: C.tan }} />
              </motion.button>
            </div>
          )}

          {/* ═══ COUPONS ═══ */}
          {activeTab === "coupons" && (
            <div className="space-y-3">
              <p className="text-[13px] mb-2" style={{ color: C.textMuted }}>
                {sampleCoupons.filter(c => c.status === "active" || c.status === "expiring").length} aktive Coupons
              </p>
              {sampleCoupons.map((coupon) => (
                <CouponCard key={coupon.id} coupon={coupon} />
              ))}
            </div>
          )}

          {/* ═══ STAMPS ═══ */}
          {activeTab === "stamps" && (
            <div className="space-y-5">
              <StampCard stamps={6} />

              <div className="p-4 rounded-[18px]" style={{ backgroundColor: C.bgCard, boxShadow: BRAND.shadows.sm }}>
                <h3 className="text-[15px] font-bold mb-3"
                  style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                  Letzte Stempel
                </h3>
                {[
                  { date: "Heute", product: "Matcha Latte", points: "+25" },
                  { date: "Gestern", product: "Cappuccino", points: "+20" },
                  { date: "24. Maerz", product: "Cheesecake + Kaffee", points: "+45" },
                  { date: "23. Maerz", product: "Pasta Pomodoro", points: "+65" },
                  { date: "22. Maerz", product: "Matcha Latte", points: "+25" },
                ].map((s, i) => (
                  <div key={i} className={`flex items-center justify-between py-3 ${i > 0 ? "border-t" : ""}`}
                    style={{ borderColor: C.border }}>
                    <div>
                      <p className="text-[14px] font-semibold" style={{ color: C.text }}>{s.product}</p>
                      <p className="text-[12px]" style={{ color: C.textMuted }}>{s.date}</p>
                    </div>
                    <span className="text-[13px] font-bold" style={{ color: C.green }}>{s.points}</span>
                  </div>
                ))}
              </div>

              <TierBadge currentTier="gaertner" points={points} />
            </div>
          )}

          {/* ═══ CHALLENGES ═══ */}
          {activeTab === "challenges" && (
            <div className="space-y-3">
              <p className="text-[13px] mb-2" style={{ color: C.textMuted }}>
                Schliesse Challenges ab und verdiene Bonus-Rewards!
              </p>
              {sampleChallenges.map((ch) => (
                <WeeklyChallenge key={ch.id} challenge={ch} />
              ))}
            </div>
          )}

          {/* ═══ BADGES ═══ */}
          {activeTab === "badges" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <img src="/loyalty/badge-stammgast.png" alt="Stammgast"
                  className="w-16 h-16 rounded-2xl object-contain" />
                <div>
                  <p className="text-[13px] font-medium" style={{ color: C.textMuted }}>
                    Gesammelt
                  </p>
                  <p className="text-xl font-extrabold" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    {sampleAchievements.filter(a => a.unlocked).length}/{sampleAchievements.length} Badges
                  </p>
                </div>
              </div>

              {/* Unlocked */}
              <div>
                <h3 className="text-[14px] font-bold mb-2" style={{ color: C.green }}>
                  Freigeschaltet
                </h3>
                <div className="space-y-2">
                  {sampleAchievements.filter(a => a.unlocked).map((b) => (
                    <AchievementBadge key={b.id} badge={b} />
                  ))}
                </div>
              </div>

              {/* In Progress */}
              <div>
                <h3 className="text-[14px] font-bold mb-2" style={{ color: C.orange }}>
                  In Arbeit
                </h3>
                <div className="space-y-2">
                  {sampleAchievements.filter(a => !a.unlocked && a.progress).map((b) => (
                    <AchievementBadge key={b.id} badge={b} />
                  ))}
                </div>
              </div>

              {/* Locked */}
              <div>
                <h3 className="text-[14px] font-bold mb-2" style={{ color: C.textMuted }}>
                  Noch gesperrt
                </h3>
                <div className="flex flex-wrap gap-1">
                  {sampleAchievements.filter(a => !a.unlocked && !a.progress).map((b) => (
                    <AchievementBadge key={b.id} badge={b} compact />
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Scratch Card Overlay */}
      <AnimatePresence>
        {showScratchCard && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6"
            style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
            onClick={() => setShowScratchCard(false)}>
            <motion.div
              initial={{ scale: 0.8, y: 30 }} animate={{ scale: 1, y: 0 }}
              onClick={e => e.stopPropagation()}
              className="text-center">
              <h2 className="text-white text-xl font-bold mb-4"
                style={{ fontFamily: BRAND.typography.heading }}>
                Dein Mystery Reward!
              </h2>
              <ScratchCard
                reward={{ emoji: "🍵", title: "Gratis Matcha Latte", description: "Dein Überraschungs-Reward!" }}
                onReveal={() => {}}
              />
              <button onClick={() => setShowScratchCard(false)}
                className="mt-6 text-white/70 text-sm font-medium">
                Schliessen
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
